import { fetcher } from '@/api/base/fetcher'
import type {
  BaseAgentRequest,
  UpdateAgentRequest,
  SearchAgentRequest,
  AgentListResponse,
  AgentRes,
  PublishAgentRequest,
  HomeDisplayAgentRequest,
  HomeDisplayAgentResponse,
} from '@/api/types/agent.type'

/**
 * 智能体相关API路径
 */
export const AGENT_API_PATHS = {
  /* 搜索所有智能体 */
  SEARCH_ALL: '/agent/agent/search',
  /* 搜索我的智能体 */
  SEARCH_MY: '/agent/agent/searchMyAgent',
  /* 根据ID查询智能体 */
  GET_BY_ID: '/agent/agent/getById',
  /* 根据AGENT_ID查询草稿智能体 */
  GET_DRAFT_BY_AGENT_ID: '/agent/agent/getDraftByAgentId',
  /* 新增智能体 */
  SAVE: '/agent/agent/save',
  /* 编辑智能体 */
  UPDATE: '/agent/agent/updateById',
  /* 删除智能体 */
  DELETE: '/agent/agent/deleteByAgentId',
  /* 发布智能体 */
  PUBLISH: '/agent/agent/publish',
  /* 首页展示智能体 */
  GET_HOME_AGENTS: '/agent/agent/homeDisplay',
} as const

/**
 * 智能体服务
 */
export const agentService = {
  /**
   * 搜索所有智能体列表
   * @param params 搜索参数
   * @returns 智能体列表
   */
  searchAllAgents: async (
    params: SearchAgentRequest
  ): Promise<AgentListResponse> => {
    const response = await fetcher.get<AgentListResponse>(
      AGENT_API_PATHS.SEARCH_ALL,
      {
        params: params as Record<string, string>,
      }
    )
    return response
  },

  /**
   * 搜索我的智能体列表
   * @param params 搜索参数
   * @returns 我的智能体列表
   */
  searchMyAgents: async (
    params: SearchAgentRequest
  ): Promise<AgentListResponse> => {
    const response = await fetcher.get<AgentListResponse>(
      AGENT_API_PATHS.SEARCH_MY,
      {
        params: params as Record<string, string>,
      }
    )
    return response
  },

  /**
   * 根据ID查询智能体详情
   * @param id 查询参数
   * @returns 智能体详情
   */
  getAgentById: async (id: string): Promise<AgentRes> => {
    const response = await fetcher.get<AgentRes>(AGENT_API_PATHS.GET_BY_ID, {
      params: { id },
    })
    return response
  },

  /**
   * 根据AGENT_ID查询草稿智能体
   * @param agentId 查询参数
   * @param options 请求选项，包含 AbortController
   * @returns 草稿智能体
   */
  getDraftByAgentId: async (
    agentId: string,
    options?: { signal?: AbortSignal }
  ): Promise<AgentRes> => {
    const response = await fetcher.get<AgentRes>(
      AGENT_API_PATHS.GET_DRAFT_BY_AGENT_ID,
      {
        params: { agentId },
        signal: options?.signal,
      }
    )
    return response
  },

  /**
   * 新增智能体
   * @param data 智能体数据
   * @returns 新增结果
   */
  saveAgent: async (data: BaseAgentRequest): Promise<null> => {
    const response = await fetcher.post<null>(AGENT_API_PATHS.SAVE, data)
    return response
  },

  /**
   * 编辑智能体
   * @param data 智能体数据
   * @returns 编辑结果
   */
  updateAgent: async (data: UpdateAgentRequest): Promise<null> => {
    const response = await fetcher.post<null>(AGENT_API_PATHS.UPDATE, data)
    return response
  },

  /**
   * 删除智能体
   * @param agentId 智能体ID
   * @returns 删除结果
   */
  deleteAgent: async (agentId: string): Promise<null> => {
    const response = await fetcher.get<null>(AGENT_API_PATHS.DELETE, {
      params: {
        agentId,
      },
    })
    return response
  },

  /**
   * 发布智能体
   * @param data 发布参数
   * @returns 发布结果
   */
  publishAgent: async (data: PublishAgentRequest): Promise<null> => {
    const response = await fetcher.post<null>(AGENT_API_PATHS.PUBLISH, data)
    return response
  },

  /**
   * 获取首页展示智能体
   * @param params 查询参数
   * @returns 首页展示智能体数据
   */
  getHomeDisplayAgents: async (
    params: HomeDisplayAgentRequest
  ): Promise<HomeDisplayAgentResponse> => {
    const response = await fetcher.get<HomeDisplayAgentResponse>(
      AGENT_API_PATHS.GET_HOME_AGENTS,
      {
        params: params as Record<string, string>,
      }
    )
    return response
  },
}
